// Constants
import { LearnMoreLinks } from "@ms/uno-constants/lib/local/AppConstants";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Controls
import { Label, MenuButton } from "@fluentui/react-components";
import { ContextualMenu } from "@ms/planner-generic-controls/lib/local/contextualMenu/ContextualMenu";
import { CheckMark, ChevronDown } from "@ms/planner-generic-controls/lib/local/icons";
import { SecureLink } from "@ms/planner-generic-controls/lib/local/secureLink/SecureLink";
// Styles
import { MenuOffset, useStyles } from "./SensitivityLabelPicker.styles";
// Utilities
import { customizeStyles } from "@ms/planner-generic-controls/lib/local/utilities/StyleUtilities";
import isEmpty from "lodash/isEmpty";
import * as React from "react";
/**
 * Position of the learn more link
 */ export var LearnMoreLinkPosition = /*#__PURE__*/ function(LearnMoreLinkPosition) {
    /** Show link beside the label (inline) */ LearnMoreLinkPosition["Beside"] = "beside";
    /** Show link below the field */ LearnMoreLinkPosition["Below"] = "below";
    return LearnMoreLinkPosition;
}({});
/**
 * SensitivityLabelPicker component for selecting sensitivity labels
 */ export const SensitivityLabelPicker = ({ sensitivityLabelTitle, userAccessibleTenantSensitivityLabels, onSensitivitySelected, canChangeSensitivity, customStyles, learnMorePosition = "beside", currentSensitivityLabelId })=>{
    const defaultStyles = useStyles();
    const styles = customizeStyles(defaultStyles, customStyles);
    /**
     * Called when user selects a sensitivity label from the menu
     */ const handleSensitivityChange = (sensitivityLabel)=>{
        onSensitivitySelected(sensitivityLabel);
    };
    /**
     * Convert sensitivity labels to nested menu items for ContextualMenu
     */ const getSensitivityMenuItems = ()=>{
        if (!userAccessibleTenantSensitivityLabels) {
            return [];
        }
        return userAccessibleTenantSensitivityLabels.map((sensitivityLabel, index)=>{
            const isParentSelected = sensitivityLabel.id === currentSensitivityLabelId;
            const hasSublabels = !isEmpty(sensitivityLabel.sublabels);
            const menuItem = {
                key: `sensitivityLabel-${index}`,
                content: sensitivityLabel.displayName,
                icon: isParentSelected ? /*#__PURE__*/ React.createElement(CheckMark, null) : undefined
            };
            if (hasSublabels) {
                // Create nested menu item with submenu
                menuItem.submenuItems = sensitivityLabel.sublabels.map((sublabel, subIndex)=>({
                        key: `sublabel-${index}-${subIndex}`,
                        content: sublabel.displayName,
                        icon: sublabel.id === currentSensitivityLabelId ? /*#__PURE__*/ React.createElement(CheckMark, null) : undefined,
                        onClick: ()=>handleSensitivityChange(sublabel)
                    }));
            } else {
                // Single menu item without submenu
                menuItem.onClick = ()=>handleSensitivityChange(sensitivityLabel);
            }
            return menuItem;
        });
    };
    /**
     * Render the learn more link
     */ const renderLearnMoreLink = ()=>{
        return /*#__PURE__*/ React.createElement(SecureLink, {
            href: LearnMoreLinks.sensitivityLabels,
            className: styles.learnMore
        }, Strings.SensitivityLabels_PlannerLearnMoreLink);
    };
    // If user cannot change sensitivity, show current label as read-only text
    if (!canChangeSensitivity) {
        return /*#__PURE__*/ React.createElement("div", {
            className: styles.root,
            "data-testid": "sensitivity-label-picker"
        }, /*#__PURE__*/ React.createElement("div", {
            className: styles.sensitivityTitle
        }, /*#__PURE__*/ React.createElement(Label, {
            size: "small"
        }, Strings.SensitivityLabels_LabelText)), /*#__PURE__*/ React.createElement("div", {
            className: styles.readOnlyValue
        }, sensitivityLabelTitle || Strings.SensitivityMenuButton_DefaultText));
    }
    const buttonText = sensitivityLabelTitle || /*#__PURE__*/ React.createElement("span", {
        className: styles.placeholder
    }, Strings.SensitivityMenuButton_DefaultText);
    return /*#__PURE__*/ React.createElement("div", {
        className: styles.root,
        "data-testid": "sensitivity-label-picker"
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.sensitivityTitle
    }, /*#__PURE__*/ React.createElement(Label, {
        size: "small",
        "aria-describedby": learnMorePosition === "beside" ? "sensitivity-label-learn-more" : undefined
    }, Strings.SensitivityLabels_LabelText), learnMorePosition === "beside" && renderLearnMoreLink()), /*#__PURE__*/ React.createElement("div", {
        className: styles.dropdownWrapper
    }, /*#__PURE__*/ React.createElement(ContextualMenu, {
        dataTestId: "sensitivity-label-picker-menu",
        positioning: {
            matchTargetSize: "width",
            offset: MenuOffset
        },
        trigger: /*#__PURE__*/ React.createElement(MenuButton, {
            "data-testid": "sensitivity-label-picker-menu-button",
            role: "combobox",
            className: styles.dropdown,
            menuIcon: /*#__PURE__*/ React.createElement(ChevronDown, null),
            appearance: "outline"
        }, buttonText),
        menuItems: getSensitivityMenuItems()
    })), learnMorePosition === "below" && renderLearnMoreLink());
};
